import { Api } from "../../Api";
import { DataFrame, Method, Status } from "../../DataFrame";
import { Helpers } from "../../Helpers";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { ResponseResult } from "../FieldChangeResult";
import { IExalusConnectionService } from "../IExalusConnectionService";
import { ILoggerService } from "../Logging/ILoggerService";
import { LoggerService } from "../Logging/LoggerService";
import { DeviceTokenMetadata, IControllerNotificationsService, NotificationClientRegistration, NotificationClientType, NotificationsServiceErrorCode } from "./IControllerNotificationsService";

export class ControllerNotificationsService implements IControllerNotificationsService {

    public static readonly ServiceName: string = "NotificationsService";
    private _connection: IExalusConnectionService;
    private _logger: ILoggerService;

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        this._logger = Api.Get<ILoggerService>(LoggerService.ServiceName);
    }

    GetServiceName(): string {
        return ControllerNotificationsService.ServiceName;
    }

    async RegisterNotificationsClientAsync(registration: NotificationClientRegistration): Promise<string | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            const request = new RegisterNotificationsClientRequest({
                ClientName: registration.ClientName,
                DeviceToken: registration.DeviceToken,
                LavvaUserName: registration.LavvaUserName,
                ClientType: this.getClientType(),
                NotificationsEnabledForToken: registration.EnableNotifications
            });

            const result = await this._connection?.SendAndWaitForResponseAsync<string>(request, 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot register notifications client - unknown error!`);
            }

            switch (result.Status) {
                case Status.WrongData: {
                    const errorCode: NotificationsServiceErrorCode = this.isValidErrorCode(result.Data) ? result.Data : NotificationsServiceErrorCode.UnknownError;
                    return new ResponseResult<NotificationsServiceErrorCode>(errorCode, `Cannot register notifications client - wrong data!`);
                }
                case Status.ResourceDoesNotExists: {
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.FeatureUnsupported, `Notifications feature not supported in controller!`);
                }
                case Status.OK: {
                    if (typeof result.Data === "string")
                        return result.Data;
                    else {
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.NoData, `Cannot register notifications client - result status is OK, but no token guid presented in paylode, unknown error!`);
                    }
                }
                default:
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot register notifications client - unknown error!`);
            }

        }
        catch (error) {
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot register notifications client - error: ${error}`);
        }
    }

    async UpdateNotificationsClientAsync(tokenGuid: string, registration: NotificationClientRegistration): Promise<Status.OK | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            const request = new UpdateNotificationsClientRequest({
                ClientName: registration.ClientName,
                DeviceToken: registration.DeviceToken,
                LavvaUserName: registration.LavvaUserName,
                ClientType: this.getClientType(),
                NotificationsEnabledForToken: registration.EnableNotifications,
                Guid: tokenGuid
            });

            const result = await this._connection?.SendAndWaitForResponseAsync<string>(request, 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot update notifications client - unknown error!`);
            }

            switch (result.Status) {
                case Status.WrongData: {
                    const errorCode: NotificationsServiceErrorCode = this.isValidErrorCode(result.Data) ? result.Data : NotificationsServiceErrorCode.UnknownError;
                    return new ResponseResult<NotificationsServiceErrorCode>(errorCode, `Cannot update notifications client - wrong data!`);
                }
                case Status.ResourceDoesNotExists: {
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.FeatureUnsupported, `Notifications feature not supported in controller!`);
                }
                case Status.OK: {
                    if (typeof result.Data === "string")
                        if (result.Data == tokenGuid)
                            return Status.OK;
                        else {
                            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot update notifications client - result status is OK, but token guid is different! request guid ${tokenGuid}, response guid: ${result.Data}`);
                        }
                    else {
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.NoData, `Cannot update notifications client - result status is OK, but no token guid presented in paylode, unknown error!`);
                    }
                }
                default:
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot update notifications client - unknown error!`);
            }

        }
        catch (error) {
            this._logger.Error(`Cannot update notifications client - error: ${error}`);
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot update notifications client - error: ${error}`);
        }
    }

    async GetRegisteredNotificationsClientsMetadataAsync(lavvaUserName?: string): Promise<DeviceTokenMetadata[] | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<DeviceTokenMetadataFrame[]>(new GetTokenMetadataRequest(), 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot get notifications clients metadata - unknown error!`);
            }

            if (result.Status == Status.ResourceDoesNotExists)
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.FeatureUnsupported, `Notifications feature not supported in controller!`);

            if (result.Status != Status.OK)
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot get notifications clients metadata - response status: ${result.Status}}`);

            if (result.Data == null)
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.NoData, `Cannot get notifications clients metadata - response status is OK, but no data!`);

            return result.Data.map((item) => {
                const registration = new DeviceTokenMetadata();
                registration.ClientName = item.ClientName;
                registration.LavvaUserName = item.LavvaUserName;
                registration.ClientType = item.ClientType;
                registration.TokenGuid = item.TokenGuid;
                registration.Token = item.Token;
                registration.RegistrationDate = new Date(item.RegistrationDate);

                return registration;
            });

        } catch (error) {
            this._logger.Error(`Cannot get notifications clients metadata - error: ${error}`);
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot get notifications clients metadata - error: ${error}`);
        }
    }

    async IsNotificationsEnabledForAllUserTokensAsync(lavvaUserName: string): Promise<boolean | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<boolean>(new IsNotificationsEnabledForAllUsersRequest(lavvaUserName), 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot get notifications status - unknown error!`);
            }

            switch (result.Status) {
                case Status.ResourceDoesNotExists:
                case Status.WrongData: {
                    if (result.Data == null)
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.FeatureUnsupported, `Notifications feature not supported in controller!`);
                    else {
                        const errorCode: NotificationsServiceErrorCode = this.isValidErrorCode(result.Data) ? result.Data : NotificationsServiceErrorCode.UnknownError;
                        return new ResponseResult<NotificationsServiceErrorCode>(errorCode, `Cannot get notifications status!`);
                    }
                }
                case Status.OK: {
                    if (typeof result.Data === "boolean")
                        return result.Data;
                    else
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.NoData, `Cannot get notifications status - result status is OK, but no data, or data type is wrong!`);
                }
                default:
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot get notifications - response code ${result.Status}}!`);
            }


        } catch (error) {
            this._logger.Error(`Cannot get notifications status - error: ${error}`);
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot get notifications status - error: ${error}`);
        }
    }

    async IsNotificationsEnabledAsync(tokenIdentity: string): Promise<boolean | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<boolean>(new IsNotificationsEnabledRequest(tokenIdentity), 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot get notifications status - unknown error!`);
            }

            switch (result.Status) {
                case Status.ResourceDoesNotExists: {
                    if (result.Data == null)
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.FeatureUnsupported, `Notifications feature not supported in controller!`);
                    else {
                        const errorCode: NotificationsServiceErrorCode = this.isValidErrorCode(result.Data) ? result.Data : NotificationsServiceErrorCode.UnknownError;
                        return new ResponseResult<NotificationsServiceErrorCode>(errorCode, `Cannot get notifications status - resource not exists!`);
                    }
                }
                case Status.OK: {
                    if (typeof result.Data === "boolean")
                        return result.Data;
                    else
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.NoData, `Cannot get notifications status - result status is OK, but no data, or data type is wrong!`);
                }
                default:
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot get notifications - response code ${result.Status}}!`);
            }

        } catch (error) {
            this._logger.Error(`Cannot get notifications status - error: ${error}`);
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot get notifications status - error: ${error}`);
        }
    }

    async EnableNotificationsAsync(tokenIdentity?: string): Promise<Status.OK | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            let tk: string | null = null;

            if (tokenIdentity != undefined && tokenIdentity != "")
                tk = tokenIdentity;

            const result = await this._connection?.SendAndWaitForResponseAsync<string>(new EnableNotificationsRequest(tk), 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot enable notifications - unknown error!`);
            }

            switch (result.Status) {
                case Status.ResourceDoesNotExists: {
                    if (result.Data == null)
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.FeatureUnsupported, `Notifications feature not supported in controller!`);
                    else {
                        const errorCode: NotificationsServiceErrorCode = this.isValidErrorCode(result.Data) ? result.Data : NotificationsServiceErrorCode.UnknownError;
                        return new ResponseResult<NotificationsServiceErrorCode>(errorCode, `Cannot enable notifications - resource not exists!`);
                    }
                }
                case Status.OK: {
                    return Status.OK;
                }
                default:
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot enable notifications - response code ${result.Status}}!`);
            }

        } catch (error) {
            this._logger.Error(`Cannot enable notifications - error: ${error}`);
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot enable notifications - error: ${error}`);

        }
    }

    async EnableNotificationsForAllUserTokensAsync(lavvaUserName: string): Promise<Status.OK | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<string>(new EnableNotificationsForLavvaUserRequest(lavvaUserName), 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot enable notifications - unknown error!`);
            }

            switch (result.Status) {
                case Status.ResourceDoesNotExists: 
                case Status.WrongData: {
                    if (result.Data == null)
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.FeatureUnsupported, `Notifications feature not supported in controller!`);
                    else {
                        const errorCode: NotificationsServiceErrorCode = this.isValidErrorCode(result.Data) ? result.Data : NotificationsServiceErrorCode.UnknownError;
                        return new ResponseResult<NotificationsServiceErrorCode>(errorCode, `Cannot enable notifications!`);
                    }
                }
                case Status.OK: {
                    return Status.OK;
                }
                default:
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot enable notifications - response code ${result.Status}!`);
            }

        } catch (error) {
            this._logger.Error(`Cannot enable notifications for lavva user ${lavvaUserName} - error: ${error}`);
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot disable notifications for lavva user ${lavvaUserName} - error: ${error}`);
        }
    }

    async DisableNotificationsAsync(tokenIdentity?: string): Promise<Status.OK | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            let tk: string | null = null;

            if (tokenIdentity != undefined && tokenIdentity != "")
                tk = tokenIdentity;

            const result = await this._connection?.SendAndWaitForResponseAsync<string>(new DisableNotificationsRequest(tk), 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot disable notifications - unknown error!`);
            }

            switch (result.Status) {
                case Status.ResourceDoesNotExists: {
                    if (result.Data == null)
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.FeatureUnsupported, `Notifications feature not supported in controller!`);
                    else {
                        const errorCode: NotificationsServiceErrorCode = this.isValidErrorCode(result.Data) ? result.Data : NotificationsServiceErrorCode.UnknownError;
                        return new ResponseResult<NotificationsServiceErrorCode>(errorCode, `Cannot disable notifications - resource not exists!`);
                    }
                }
                case Status.OK: {
                    return Status.OK;
                }
                default:
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot disable notifications - response code ${result.Status}!`);
            }

        } catch (error) {
            this._logger.Error(`Cannot disable notifications - error: ${error}`);
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot disable notifications - error: ${error}`);
        }
    }

    async DisableNotificationsForAllUserTokensAsync(lavvaUserName: string): Promise<Status.OK | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<string>(new DisableNotificationsForLavvaUserRequest(lavvaUserName), 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot disable notifications - unknown error!`);
            }

            switch (result.Status) {
                case Status.ResourceDoesNotExists: 
                case Status.WrongData:{
                    if (result.Data == null)
                        return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.FeatureUnsupported, `Notifications feature not supported in controller!`);
                    else {
                        const errorCode: NotificationsServiceErrorCode = this.isValidErrorCode(result.Data) ? result.Data : NotificationsServiceErrorCode.UnknownError;
                        return new ResponseResult<NotificationsServiceErrorCode>(errorCode, `Cannot disable notifications!`);
                    }
                }
                case Status.OK: {
                    return Status.OK;
                }
                default:
                    return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot disable notifications - response code ${result.Status}!`);
            }

        } catch (error) {
            this._logger.Error(`Cannot disable notifications for lavva user ${lavvaUserName} - error: ${error}`);
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot disable notifications for lavva user ${lavvaUserName} - error: ${error}`);
        }
    }

    async DeleteNotificationsTokenAsync(tokenGuid: string): Promise<Status.OK | ResponseResult<NotificationsServiceErrorCode>> {
        try {
            const result = await this._connection?.SendAndWaitForResponseAsync<any>(new DeleteNotificationsTokenRequest(tokenGuid), 8000, false);

            if (result == null || result.Status == null) {
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot delete notifications token - unknown error!`);
            }

            if(result.Status == Status.OK)
                return Status.OK;
            else
                return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot delete notifications token - response code ${result.Status}!`);
            
        } catch (error) {
            this._logger.Error(`Cannot delete notifications token - error: ${error}`);
            return new ResponseResult<NotificationsServiceErrorCode>(NotificationsServiceErrorCode.UnknownError, `Cannot delete notifications token - error: ${error}`);
        }
    }

    private getClientType(): NotificationClientType {
        if (Helpers.IsIosNative()) {
            return NotificationClientType.iOS;
        }
        else if (navigator.userAgent.endsWith("LavvaAndroidNative")) {
            return NotificationClientType.Android;
        }
        else {
            return NotificationClientType.Web;
        }
    }

    private isValidErrorCode(value: any): value is NotificationsServiceErrorCode {
        return Object.values(NotificationsServiceErrorCode).includes(value);
    }
}

class RegisterNotificationsClientRequest extends DataFrame<RegisterNotificationsClientFrame> {
    constructor(data: RegisterNotificationsClientFrame) {
        super();
        this.Resource = "/notifications/token";
        this.Method = Method.Post;
        this.Data = data;
    }
}

class RegisterNotificationsClientFrame {
    ClientName: string = "";
    LavvaUserName: string = "";
    DeviceToken: string = "";
    ClientType: NotificationClientType = NotificationClientType.Unknown;
    NotificationsEnabledForToken: boolean = true;
}

class UpdateNotificationsClientRequest extends DataFrame<RegisterNotificationsClientFrame> {
    constructor(data: UpdateNotificationsClientFrame) {
        super();
        this.Resource = "/notifications/token";
        this.Method = Method.Put;
        this.Data = data;
    }
}

class UpdateNotificationsClientFrame extends RegisterNotificationsClientFrame {
    Guid: string = "";
}

class GetTokenMetadataRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/notifications/tokens/metadata";
        this.Method = Method.Get;
    }
}

class DeviceTokenMetadataFrame {
    ClientName: string = "";
    LavvaUserName: string = "";
    ClientType: NotificationClientType = NotificationClientType.Unknown;
    TokenGuid: string = "";
    Token: string = "";
    RegistrationDate: string = "";
}

class IsNotificationsEnabledRequest extends DataFrame<any> {
    constructor(tokenIdentity: string) {
        super();
        this.Resource = "/notifications/status";
        this.Method = Method.Get;
        this.Data = {
            TokenIdentity: tokenIdentity
        };
    }
}

class EnableNotificationsRequest extends DataFrame<any> {
    constructor(tokenIdentity: string | null) {
        super();
        this.Resource = "/notifications/enable";
        this.Method = Method.Post;
        this.Data = tokenIdentity
    }
}

class DisableNotificationsRequest extends DataFrame<any> {
    constructor(tokenIdentity: string | null) {
        super();
        this.Resource = "/notifications/disable";
        this.Method = Method.Post;
        this.Data = tokenIdentity
    }
}

class EnableNotificationsForLavvaUserRequest extends DataFrame<any> {
    constructor(lavvaUser: string | null) {
        super();
        this.Resource = "/notifications/enable/for/lavva/user";
        this.Method = Method.Post;
        this.Data = lavvaUser
    }
}

class DisableNotificationsForLavvaUserRequest extends DataFrame<any> {
    constructor(lavvaUser: string | null) {
        super();
        this.Resource = "/notifications/disable/for/lavva/user";
        this.Method = Method.Post;
        this.Data = lavvaUser
    }
}

class IsNotificationsEnabledForAllUsersRequest extends DataFrame<any> {
    constructor(lavvaUser: string) {
        super();
        this.Resource = "/notifications/status/for/lavva/user";
        this.Method = Method.Get;
        this.Data = lavvaUser
    }
}

class DeleteNotificationsTokenRequest extends DataFrame<any> {
    constructor(tokenGuid: string) {
        super();
        this.Resource = `/notifications/token`;
        this.Method = Method.Delete;
        this.Data = tokenGuid;
    }
}